import '../styles/Dashboard.css';

import React, { useState } from 'react';

import {
  addDoc,
  collection,
  getDocs,
  getFirestore,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore';

import { useAuth } from '../contexts/AuthContext';
import firebase from '../firebase';
import {
  Button,
  Card,
  Container,
  Typography,
} from './common';
import SessionsList from './SessionsList';

function generateRandomCode() {
  // Generate a 6-character alphanumeric code
  const characters = 'ABCDEFGHKMNPQRSTUVWXYZ23456789';
  let result = '';
  for (let i = 0; i < 6; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
}


export default function Dashboard() {
  const { currentUser, logout } = useAuth();
  const [sessionCode, setSessionCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  
  const db = getFirestore(firebase);

  async function handleCreateSession(): Promise<void> {
    try {
      setLoading(true);
      setError('');
      
      const code = generateRandomCode();
      const codeUpperCase = code.toUpperCase();
      
      // Check if a session with this code already exists
      const sessionsRef = collection(db, "sessions");
      const q = query(sessionsRef, where("code", "==", codeUpperCase), where("active", "==", true));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        // If a session with this code already exists, generate a new code
        console.log('Session with code already exists, generating new code...');
        return handleCreateSession();
      }
      
      // Create session in Firestore
      await addDoc(collection(db, "sessions"), {
        code: codeUpperCase,
        createdBy: currentUser?.uid,
        createdAt: serverTimestamp(),
        coaches: [currentUser?.uid],
        active: true
      });
      
      setSessionCode(code);
    } catch (error) {
      setError('Failed to create session');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  const handleCopyLink = () => {
    const joinUrl = `${window.location.origin}/join?code=${sessionCode}`;
    navigator.clipboard.writeText(joinUrl);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleOpenLink = () => {
    const joinUrl = `${window.location.origin}/join?code=${sessionCode}`;
    window.open(joinUrl, '_blank');
  };

  return (
    <Container>
      <Card>
        <Typography variant="h2">Admin Dashboard</Typography>
        <Typography variant="body">Logged in as: {currentUser?.email}</Typography>
        
        <div className="session-generator">
          <Typography variant="h3">Create New Session</Typography>
          {error && <div className="error-message">{error}</div>}
          
          <Button 
            onClick={handleCreateSession} 
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create New Session'}
          </Button>
          
          {sessionCode && (
            <Card variant="success" className="session-code">
              <Typography variant="h3">Session Code:</Typography>
              <div className="code-display">
                {sessionCode}
              </div>
              <Typography>Share this code with other coaches to join the session</Typography>
              <div className="share-buttons">
                <Button 
                  variant="secondary" 
                  onClick={handleCopyLink}
                >
                  {copySuccess ? 'Copied!' : 'Copy Link'}
                </Button>
                <Button 
                  variant="secondary" 
                  onClick={handleOpenLink}
                >
                  Open Link
                </Button>
              </div>
            </Card>
          )}
        </div>
        
        <SessionsList />
        
        <Button 
          variant="danger" 
          onClick={() => logout()}
        >
          Logout
        </Button>
      </Card>
    </Container>
  );
}